import React from "react"

const actionBase = {
  minHeight: 72,
  borderRadius: 14,
  padding: "12px 13px",
  display: "flex",
  alignItems: "center",
  gap: 11,
  textAlign: "left",
  fontWeight: 900,
  cursor: "pointer",
  transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)"
}

const actionTones = {
  blue: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    iconBg: "#ffffff",
    iconBorder: "#bfdbfe"
  },
  green: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#166534",
    iconBg: "#ffffff",
    iconBorder: "#bbf7d0"
  },
  slate: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    color: "#334155",
    iconBg: "#f8fafc",
    iconBorder: "#e2e8f0"
  },
  danger: {
    background: "#fff1f2",
    border: "1px solid #fecaca",
    color: "#be123c",
    iconBg: "#ffffff",
    iconBorder: "#fecaca"
  }
}

function ActionButton({
  icon,
  title,
  helper,
  tone = "slate",
  disabled = false,
  onClick
}) {
  const palette = actionTones[tone] || actionTones.slate

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      style={{
        ...actionBase,
        background: disabled ? "#f8fafc" : palette.background,
        border: disabled ? "1px solid #e2e8f0" : palette.border,
        color: disabled ? "#94a3b8" : palette.color,
        opacity: disabled ? 0.72 : 1,
        cursor: disabled || !onClick ? "not-allowed" : "pointer"
      }}
      title={helper}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: disabled ? "#ffffff" : palette.iconBg,
          border: disabled ? "1px solid #e2e8f0" : `1px solid ${palette.iconBorder}`,
          fontSize: 15
        }}
      >
        {icon}
      </span>

      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 13.5,
            lineHeight: 1.15,
            letterSpacing: "-0.01em"
          }}
        >
          {title}
        </span>

        {helper ? (
          <span
            style={{
              display: "block",
              marginTop: 4,
              fontSize: 11.5,
              lineHeight: 1.3,
              color: disabled ? "#94a3b8" : "#64748b",
              fontWeight: 700
            }}
          >
            {helper}
          </span>
        ) : null}
      </span>
    </button>
  )
}

export default function PatientQuickActions({
  stacked = false,
  isReadOnly = false,
  readOnlyReason = "",
  onScheduleAppointment,
  onSchedule,
  onRegisterPayment,
  onOpenPayment,
  onOpenPaymentDrawer,
  onOpenQuickPayment,
  onQuickPayment,
  onCreatePayment,
  onEditPatient,
  onOpenEditPatient,
  onEdit,
  onDeletePatient
}) {
  const scheduleHandler = onScheduleAppointment || onSchedule
  const paymentHandler =
    onRegisterPayment ||
    onOpenPayment ||
    onOpenPaymentDrawer ||
    onOpenQuickPayment ||
    onQuickPayment ||
    onCreatePayment

  const editHandler = onEditPatient || onOpenEditPatient || onEdit
  const disabled = Boolean(isReadOnly)

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 22,
        padding: 14,
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 11,
            fontWeight: 950,
            letterSpacing: 0.55,
            textTransform: "uppercase"
          }}
        >
          Acciones rápidas
        </div>

        <div
          style={{
            borderRadius: 999,
            border: disabled ? "1px solid #e2e8f0" : "1px solid #bfdbfe",
            background: disabled ? "#f8fafc" : "#eff6ff",
            color: disabled ? "#94a3b8" : "#1d4ed8",
            padding: "4px 9px",
            fontSize: 11,
            fontWeight: 900
          }}
        >
          {disabled ? "Solo lectura" : "Activo"}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: stacked ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 9
        }}
      >
        <ActionButton
          icon="📅"
          title="Agendar cita"
          helper="Enviar a agenda"
          tone="blue"
          disabled={disabled}
          onClick={disabled ? null : scheduleHandler}
        />

        <ActionButton
          icon="💳"
          title="Registrar pago"
          helper="Abrir cobro rápido"
          tone="green"
          disabled={disabled}
          onClick={disabled ? null : paymentHandler}
        />

        <ActionButton
          icon="✏️"
          title="Editar paciente"
          helper="Actualizar datos"
          tone="slate"
          disabled={disabled}
          onClick={disabled ? null : editHandler}
        />

        <ActionButton
          icon="🗑️"
          title="Eliminar paciente"
          helper={disabled ? readOnlyReason || "No disponible" : "Borrado seguro"}
          tone="danger"
          disabled={disabled}
          onClick={disabled ? null : onDeletePatient}
        />
      </div>
    </div>
  )
}
